import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { Asset, AssetRateCard, AssetTaskType, ProjectSetup, ShotStatus } from "../types";
import { formatCurrency, cn } from "../utils";
import { toast } from "sonner";
import { Plus, Copy, Trash2, Search, CheckSquare, Square, Image as ImageIcon, Edit3, ArrowUp, ArrowDown, ArrowUpDown, Sparkles, Loader2, Upload, GripVertical, Filter, X, FoldVertical, Database, ChevronDown } from "lucide-react";
import { GoogleGenAI } from "@google/genai";
import { AssetBulkEditModal } from "./AssetBulkEditModal";
import { AssetDetailModal } from "./AssetDetailModal";
import { generateThumbnail } from "../services/aiService";
import { SectionPillHeader } from "./SectionPillHeader";
import { ExportFilterState } from "../lib/bidVersioning";
import { BID_ITEM_STATUSES, getBidStatusClasses, getBidStatusValue, isBidItemIncluded, isExcludedBidStatus } from "../lib/bidStatus";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function ResizableTh({
  id,
  width,
  onResizeStart,
  children,
  className,
  title,
  style,
}: {
  id: string;
  width: number;
  onResizeStart: (e: React.MouseEvent, id: string) => void;
  children: React.ReactNode;
  className?: string;
  title?: string;
  style?: React.CSSProperties;
}) {
  return (
    <th
      className={cn("relative group", className)}
      style={{ width, minWidth: width, maxWidth: width, ...style }}
      title={title}
    >
      {children}
      <div
        className="absolute right-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-indigo-400 active:bg-indigo-600 z-10 opacity-0 group-hover:opacity-100 transition-opacity"
        onMouseDown={(e) => onResizeStart(e, id)}
      />
    </th>
  );
}

function AutoResizeTextarea({
  value,
  onChange,
  onKeyDown,
  placeholder,
  readOnly,
  className,
}: {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  readOnly?: boolean;
  className?: string;
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [value]);

  return (
    <textarea
      ref={textareaRef}
      value={value}
      onChange={onChange}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      readOnly={readOnly}
      rows={1}
      className={cn("overflow-hidden resize-none break-words", className)}
    />
  );
}

interface FilterCriterion {
  id: string;
  field: string;
  operator: 'contains' | 'not_contains' | 'equals' | 'not_equals' | 'gt' | 'lt' | 'gte' | 'lte' | 'is_empty' | 'is_not_empty';
  value: string;
}

const FILTER_FIELDS = [
  { label: 'Asset Name', value: 'assetCode' },
  { label: 'Type', value: 'type' },
  { label: 'Description', value: 'description' },
  { label: 'Scope of Work', value: 'scopeOfWork' },
  { label: 'Status', value: 'status' },
  { label: 'Total Days', value: 'totalDays' },
  { label: 'Total Cost', value: 'totalCost' },
  { label: 'Quantity', value: 'quantity' },
  { label: 'Concept Days', value: 'days.concept' },
  { label: 'DMP Days', value: 'days.dmp' },
  { label: 'Model Days', value: 'days.model' },
  { label: 'Rig Days', value: 'days.rig' },
  { label: 'Retop Days', value: 'days.retop' },
  { label: 'Cloth Days', value: 'days.cloth' },
  { label: 'Fur Days', value: 'days.fur' },
  { label: 'Groom Days', value: 'days.groom' },
  { label: 'Texture Days', value: 'days.texture' },
  { label: 'Anim Days', value: 'days.anim' },
  { label: 'FX Days', value: 'days.fx' },
  { label: 'Lookdev Days', value: 'days.lookdev' },
  { label: 'Photoscan Days', value: 'days.photoscan' },
];

const OPERATORS = [
  { label: 'Contains', value: 'contains' },
  { label: 'Not Contains', value: 'not_contains' },
  { label: 'Equals', value: 'equals' },
  { label: 'Not Equals', value: 'not_equals' },
  { label: 'Greater Than', value: 'gt' },
  { label: 'Less Than', value: 'lt' },
  { label: 'Greater Than or Equal', value: 'gte' },
  { label: 'Less Than or Equal', value: 'lte' },
  { label: 'Is Empty', value: 'is_empty' },
  { label: 'Is Not Empty', value: 'is_not_empty' },
];

interface Props {
  assets: Asset[];
  rates: AssetRateCard;
  setup: ProjectSetup;
  onChange: (assets: Asset[] | ((prev: Asset[]) => Asset[])) => void;
  onAddAsset: () => void;
  onDuplicateAsset: (id: string) => void;
  onDeleteAsset: (id: string) => void;
  readOnly?: boolean;
  onFilterStateChange?: (filterState: ExportFilterState) => void;
}

interface SortableAssetRowProps {
  asset: Asset;
  index: number;
  calculateAssetTotal: (asset: Asset) => number;
  isSelected: boolean;
  toggleSelect: (id: string) => void;
  handleAssetChange: (id: string, field: keyof Asset, value: any) => void;
  handleDaysChange: (id: string, task: AssetTaskType, value: string) => void;
  handleThumbnailUpload: (id: string, e: React.ChangeEvent<HTMLInputElement>) => void;
  handleGenerateAI: (id: string, description: string) => void;
  handleRowDoubleClick: (asset: Asset, e: React.MouseEvent) => void;
  handleTextareaKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>, id: string, field: keyof Asset, currentValue: string) => void;
  moveAsset: (index: number, direction: 'up' | 'down') => void;
  onDuplicateAsset: (id: string) => void;
  onDeleteAsset: (id: string) => void;
  readOnly: boolean;
  colWidths: Record<string, number>;
  setup: ProjectSetup;
  search: string;
  filteredAssetsCount: number;
  generatingIds: Set<string>;
  rates: AssetRateCard;
  biddingMode: boolean;
}

const SortableAssetRow = React.memo(({
  asset,
  index,
  calculateAssetTotal,
  isSelected,
  toggleSelect,
  handleAssetChange,
  handleDaysChange,
  handleThumbnailUpload,
  handleGenerateAI,
  handleRowDoubleClick,
  handleTextareaKeyDown,
  moveAsset,
  onDuplicateAsset,
  onDeleteAsset,
  readOnly,
  colWidths,
  setup,
  search,
  filteredAssetsCount,
  generatingIds,
  rates,
  biddingMode,
}: SortableAssetRowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: asset.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    position: isDragging ? 'relative' as const : undefined,
  };

  const assetTotal = calculateAssetTotal(asset);

  return (
    <tr
      ref={setNodeRef}
      style={style}
      onDoubleClick={(e) => handleRowDoubleClick(asset, e)}
      className={cn(
        "transition-colors group cursor-default bg-white dark:bg-slate-900 overflow-hidden hover:bg-slate-50 dark:hover:bg-slate-800/70",
        isSelected && "bg-indigo-50/50 dark:bg-indigo-900/20",
        (isExcludedBidStatus(asset.status) || (!asset.status && !asset.active)) && "opacity-50",
        isDragging && "bg-white dark:bg-slate-850 shadow-lg opacity-100 ring-2 ring-indigo-500 ring-inset",
        biddingMode && "h-[80px]"
      )}
    >
      <td className={cn("px-2 align-middle sticky left-0 z-[1] bg-inherit border-l-2 border-r-2 border-b-2 border-white dark:border-slate-900", biddingMode ? "py-1" : "py-2")} style={{ left: 0 }}>
        <div className="relative flex items-center justify-center min-h-[20px] pl-5">
          {!readOnly && (
            <div
              {...attributes}
              {...listeners}
              className="absolute left-0 cursor-grab active:cursor-grabbing p-1 text-slate-300 hover:text-slate-500 dark:text-slate-600 dark:hover:text-slate-300 transition-colors"
            >
              <GripVertical className={cn("w-4 h-4", biddingMode && "w-3 h-3")} />
            </div>
          )}
          <button onClick={() => toggleSelect(asset.id)} className="flex items-center justify-center text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-200" disabled={readOnly}>
            {isSelected ? <CheckSquare className={cn("w-4 h-4 text-indigo-600", biddingMode && "w-3 h-3")} /> : <Square className={cn("w-4 h-4", biddingMode && "w-3 h-3")} />}
          </button>
        </div>
      </td>
      <td className={cn("px-4 text-center align-middle sticky z-[1] bg-inherit border-r-2 border-b-2 border-white dark:border-slate-900", biddingMode ? "py-1" : "py-2")} style={{ left: colWidths.select }}>
        <select
          value={getBidStatusValue(asset)}
          onChange={(e) => handleAssetChange(asset.id, "status", e.target.value)}
          disabled={readOnly}
          className={cn(
            "w-full px-1 py-1 text-[10px] font-bold uppercase tracking-wider border rounded focus:ring-1 focus:ring-indigo-500 outline-none transition-colors",
            getBidStatusClasses(getBidStatusValue(asset))
          )}
        >
          {BID_ITEM_STATUSES.map((status) => (
            <option key={status} value={status}>{status}</option>
          ))}
        </select>
      </td>
      <td className={cn("px-4 text-center align-middle sticky z-[1] bg-inherit border-r-2 border-b-2 border-white dark:border-slate-900", biddingMode ? "py-1" : "py-2")} style={{ left: colWidths.select + colWidths.active }}>
        <div className="flex justify-center w-full">
          <div className="relative group/thumb">
            <label className={cn(
              "relative flex items-center justify-center bg-slate-100 dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 overflow-hidden", 
              biddingMode ? "w-[72px] h-[72px]" : "w-24 h-24",
              !readOnly && "cursor-pointer hover:bg-slate-200 transition-colors"
            )}>
              {asset.thumbnail ? (
                <img src={asset.thumbnail} alt="thumb" className="w-full h-full object-cover" />
              ) : (
                <ImageIcon className={cn("text-slate-400", biddingMode ? "w-3 h-3" : "w-4 h-4")} />
              )}
              {!readOnly && (
                <>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleThumbnailUpload(asset.id, e)}
                  />
                  <div className={cn(
                    "absolute bottom-1 right-1 flex gap-1 opacity-0 group-hover/thumb:opacity-100 transition-all z-10",
                    biddingMode && "bottom-0.5 right-0.5"
                  )}>
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleGenerateAI(asset.id, asset.description); }}
                      disabled={generatingIds.has(asset.id) || !asset.description}
                      className={cn(
                        "bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm border border-slate-200 dark:border-slate-700 rounded-lg shadow-sm text-indigo-600 dark:text-indigo-300 hover:text-indigo-700 dark:hover:text-indigo-200 hover:bg-white dark:hover:bg-slate-900 transition-all",
                        biddingMode ? "p-0.5" : "p-1.5",
                        generatingIds.has(asset.id) && "opacity-100 cursor-not-allowed",
                        !asset.description && "cursor-not-allowed opacity-50"
                      )}
                      title="Generate with AI"
                    >
                      {generatingIds.has(asset.id) ? (
                        <Loader2 className={cn("animate-spin", biddingMode ? "w-2 h-2" : "w-3 h-3")} />
                      ) : (
                        <Sparkles className={cn(biddingMode ? "w-2 h-2" : "w-3 h-3")} />
                      )}
                    </button>
                    <div 
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      className={cn(
                        "bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm border border-slate-200 dark:border-slate-700 rounded-lg shadow-sm text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-slate-100 hover:bg-white dark:hover:bg-slate-900 transition-all cursor-pointer",
                        biddingMode ? "p-0.5" : "p-1.5"
                      )}
                      title="Upload Image"
                    >
                      <label className="cursor-pointer">
                        <Upload className={cn(biddingMode ? "w-2 h-2" : "w-3 h-3")} />
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleThumbnailUpload(asset.id, e)}
                        />
                      </label>
                    </div>
                  </div>
                </>
              )}
            </label>
          </div>
        </div>
      </td>
      <td className={cn("px-4 align-middle sticky z-[1] bg-inherit border-r-2 border-b-2 border-white dark:border-slate-900", biddingMode ? "py-1" : "py-2")} style={{ left: colWidths.select + colWidths.active + (biddingMode ? colWidths.thumb - 20 : colWidths.thumb) }}>
          <input
            type="text"
            value={asset.assetCode}
            onChange={(e) => handleAssetChange(asset.id, "assetCode", e.target.value)}
            placeholder="Asset Name..."
            readOnly={readOnly}
            className="w-full bg-transparent border-none p-1 focus:ring-1 focus:ring-indigo-500 rounded outline-none font-sans disabled:opacity-75 text-xs"
          />
      </td>
      {!biddingMode && (
        <td className={cn("px-4 align-middle sticky z-[1] bg-inherit border-r-2 border-b-2 border-white dark:border-slate-900", biddingMode ? "py-1" : "py-2")} style={{ left: colWidths.select + colWidths.active + colWidths.thumb + colWidths.assetCode }}>
          <select
            value={asset.type || ""}
            onChange={(e) => handleAssetChange(asset.id, "type", e.target.value)}
            disabled={readOnly}
            className="w-full bg-transparent border border-transparent hover:border-slate-200 focus:border-indigo-500 p-1 rounded outline-none text-xs disabled:opacity-75"
          >
            <option value="">Select Type...</option>
            <option value="character">character</option>
            <option value="creature">creature</option>
            <option value="crowd">crowd</option>
            <option value="environment">environment</option>
            <option value="fx">fx</option>
            <option value="dmp">dmp</option>
            <option value="prop">prop</option>
            <option value="vehicle">vehicle</option>
            <option value="groom">groom</option>
            <option value="contactSheet">contactSheet</option>
            <option value="utility">utility</option>
          </select>
        </td>
      )}
      <td className={cn(
        "px-4 text-right font-sans text-slate-900 dark:text-slate-100 align-middle sticky z-[1] bg-inherit border-r-2 border-b-2 border-white dark:border-slate-900 text-xs",
        biddingMode ? "py-1" : "py-2"
      )} style={{ left: colWidths.select + colWidths.active + (biddingMode ? colWidths.thumb - 20 : colWidths.thumb) + colWidths.assetCode + (biddingMode ? 0 : colWidths.type) }}>
        <div>{formatCurrency(assetTotal, setup.currency)}</div>
        {setup.targetCurrency && setup.exchangeRate > 0 && (
          <div className="text-slate-400 dark:text-slate-500 mt-0.5 text-xs">
            {formatCurrency(assetTotal * setup.exchangeRate, setup.targetCurrency)}
          </div>
        )}
      </td>
      {!biddingMode && (
        <>
          <td className="px-4 py-2 align-middle border-r-2 border-b-2 border-white dark:border-slate-900">
            <AutoResizeTextarea
              value={asset.description}
              onChange={(e) => handleAssetChange(asset.id, "description", e.target.value)}
              onKeyDown={(e) => handleTextareaKeyDown(e, asset.id, "description", asset.description)}
              placeholder="Description..."
              readOnly={readOnly}
              className="w-full bg-transparent border-none p-1 focus:ring-1 focus:ring-indigo-500 rounded outline-none text-xs disabled:opacity-75 min-h-[40px]"
            />
          </td>
          <td className="px-2 py-2 align-middle border-r-2 border-b-2 border-white dark:border-slate-900">
            <input
              type="number"
              min="1"
              step="1"
              value={asset.quantity || 1}
              onChange={(e) => handleAssetChange(asset.id, "quantity", parseInt(e.target.value) || 1)}
              readOnly={readOnly}
              className="w-full bg-transparent border border-transparent hover:border-slate-200 dark:hover:border-slate-600 focus:border-indigo-500 p-1 text-center rounded outline-none text-xs disabled:opacity-75"
            />
          </td>
        </>
      )}
      {(["supervisor", "concept", "dmp", "model", "rig", "retop", "cloth", "fur", "groom", "texture", "anim", "fx", "lookdev", "photoscan"] as AssetTaskType[]).map((task, i) => {
        let displayValue: number | string = asset.days[task] || "";
        let isReadOnly = readOnly;

        if (task === "supervisor") {
          const otherDays = 
            (asset.days.concept || 0) +
            (asset.days.dmp || 0) +
            (asset.days.model || 0) +
            (asset.days.rig || 0) +
            (asset.days.retop || 0) +
            (asset.days.cloth || 0) +
            (asset.days.fur || 0) +
            (asset.days.groom || 0) +
            (asset.days.texture || 0) +
            (asset.days.anim || 0) +
            (asset.days.fx || 0) +
            (asset.days.lookdev || 0) +
            (asset.days.photoscan || 0);
          
          displayValue = (otherDays * ((setup.supervisionPercentage || 0) / 100)).toFixed(2);
          if (displayValue.endsWith(".00")) displayValue = displayValue.slice(0, -3);
          isReadOnly = true;
        }

        return (
          <td key={task} className={cn("px-1 bg-slate-50 dark:bg-slate-800/80 border-r-2 border-b-2 border-white dark:border-slate-900 align-middle", (i === 0 || (biddingMode && task === "concept")) && "border-l-2", biddingMode ? "py-1" : "py-2")}>
            <input
              type="number"
              min="0"
              step="0.5"
              value={displayValue}
              onChange={(e) => handleDaysChange(asset.id, task, e.target.value)}
              readOnly={isReadOnly}
              className="w-full bg-transparent border border-transparent hover:border-slate-200 dark:hover:border-slate-600 focus:border-indigo-500 p-1 text-center rounded outline-none disabled:opacity-75 text-xs"
            />
          </td>
        );
      })}
      {!biddingMode && (
        <>
          <td className={cn("px-4 align-middle border-r-2 border-b-2 border-white dark:border-slate-900", biddingMode ? "py-1" : "py-2")}>
              <AutoResizeTextarea
                value={asset.assumptions}
                onChange={(e) => handleAssetChange(asset.id, "assumptions", e.target.value)}
                onKeyDown={(e) => handleTextareaKeyDown(e, asset.id, "assumptions", asset.assumptions)}
                placeholder="Assumptions..."
                readOnly={readOnly}
                className={cn(
                  "w-full bg-transparent border-none p-1 focus:ring-1 focus:ring-indigo-500 rounded outline-none leading-normal text-slate-500 dark:text-slate-400 disabled:opacity-75 text-xs",
                  biddingMode ? "h-[64px] resize-none overflow-hidden" : "min-h-[40px]"
                )}
              />
          </td>
          <td className={cn("px-4 align-middle border-r-2 border-b-2 border-white dark:border-slate-900", biddingMode ? "py-1" : "py-2")}>
              <AutoResizeTextarea
                value={asset.scopeOfWork || ""}
                onChange={(e) => handleAssetChange(asset.id, "scopeOfWork", e.target.value)}
                onKeyDown={(e) => handleTextareaKeyDown(e, asset.id, "scopeOfWork", asset.scopeOfWork)}
                placeholder="Scope of work..."
                readOnly={readOnly}
                className={cn(
                  "w-full bg-transparent border-none p-1 focus:ring-1 focus:ring-indigo-500 rounded outline-none leading-normal text-slate-500 dark:text-slate-400 disabled:opacity-75 text-xs",
                  biddingMode ? "h-[64px] resize-none overflow-hidden" : "min-h-[40px]"
                )}
              />
          </td>
          <td className={cn("px-4 align-middle border-r-2 border-b-2 border-white dark:border-slate-900", biddingMode ? "py-1" : "py-2")}>
              <AutoResizeTextarea
                value={asset.internalNotes}
                onChange={(e) => handleAssetChange(asset.id, "internalNotes", e.target.value)}
                onKeyDown={(e) => handleTextareaKeyDown(e, asset.id, "internalNotes", asset.internalNotes)}
                placeholder="Internal notes..."
                readOnly={readOnly}
                className={cn(
                  "w-full bg-transparent border-none p-1 focus:ring-1 focus:ring-indigo-500 rounded outline-none leading-normal text-slate-500 dark:text-slate-400 disabled:opacity-75 text-xs",
                  biddingMode ? "h-[64px] resize-none overflow-hidden" : "min-h-[40px]"
                )}
              />
          </td>
        </>
      )}
      {!readOnly && (
        <td className={cn("px-4 align-middle border-r-2 border-b-2 border-white dark:border-slate-900", biddingMode ? "py-1" : "py-2")}>
          <div className="flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={() => moveAsset(index, 'up')}
              disabled={index === 0 || search.trim() !== ""}
              className="p-1 text-slate-400 hover:text-indigo-600 rounded disabled:opacity-30 disabled:hover:text-slate-400"
              title="Move Up"
            >
              <ArrowUp className="w-4 h-4" />
            </button>
            <button
              onClick={() => moveAsset(index, 'down')}
              disabled={index === filteredAssetsCount - 1 || search.trim() !== ""}
              className="p-1 text-slate-400 hover:text-indigo-600 rounded disabled:opacity-30 disabled:hover:text-slate-400"
              title="Move Down"
            >
              <ArrowDown className="w-4 h-4" />
            </button>
            <button
              onClick={() => onDuplicateAsset(asset.id)}
              className="p-1 text-slate-400 hover:text-indigo-600 rounded"
              title="Duplicate"
            >
              <Copy className="w-4 h-4" />
            </button>
            <button
              onClick={() => onDeleteAsset(asset.id)}
              className="p-1 text-slate-400 hover:text-red-600 rounded"
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </td>
      )}
    </tr>
  );
});

export function AssetTable({
  assets,
  rates,
  setup,
  onChange,
  onAddAsset,
  onDuplicateAsset,
  onDeleteAsset,
  readOnly = false,
  onFilterStateChange,
}: Props) {
  const [search, setSearch] = useState("");
  const [biddingMode, setBiddingMode] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState<ShotStatus[]>(BID_ITEM_STATUSES);
  const [advancedFilters, setAdvancedFilters] = useState<FilterCriterion[]>([]);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [smartFilter, setSmartFilter] = useState("");
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const statusFilterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = 0;
    }
  }, [biddingMode]);
  const [isSmartFiltering, setIsSmartFiltering] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);
  const [selectedAssetIdForDetail, setSelectedAssetIdForDetail] = useState<string | null>(null);
  const [generatingIds, setGeneratingIds] = useState<Set<string>>(new Set());

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = assets.findIndex((a) => a.id === active.id);
      const newIndex = assets.findIndex((a) => a.id === over.id);
      onChange(arrayMove(assets, oldIndex, newIndex));
    }
  }, [assets, onChange]);

  const [colWidths, setColWidths] = useState<Record<string, number>>({
    select: 60,
    active: 105,
    thumb: 140,
    assetCode: 170,
    type: 120,
    description: 200,
    scopeOfWork: 200,
    qty: 80,
    assumptions: 200,
    internalNotes: 200,
    supervisor: 75,
    concept: 75,
    dmp: 75,
    model: 75,
    rig: 75,
    retop: 75,
    cloth: 75,
    fur: 75,
    groom: 75,
    texture: 75,
    anim: 75,
    fx: 75,
    lookdev: 75,
    photoscan: 75,
    total: 120,
    actions: 120
  });

  const [resizing, setResizing] = useState<{ col: string; startX: number; startWidth: number } | null>(null);

  useEffect(() => {
    if (!resizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const diff = e.pageX - resizing.startX;
      const newWidth = Math.max(40, resizing.startWidth + diff);
      setColWidths((prev) => ({ ...prev, [resizing.col]: newWidth }));
    };

    const handleMouseUp = () => {
      setResizing(null);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [resizing]);

  const visibleWidth = useMemo(() => {
    const thumbWidth = biddingMode ? colWidths.thumb - 20 : colWidths.thumb;
    let total = colWidths.select + colWidths.active + thumbWidth + colWidths.assetCode + colWidths.total + (readOnly ? 0 : colWidths.actions);
    
    if (!biddingMode) {
      total += colWidths.type + colWidths.description + colWidths.scopeOfWork + colWidths.qty + colWidths.supervisor + colWidths.assumptions + colWidths.internalNotes;
    }
    
    const tasks: AssetTaskType[] = ["concept", "dmp", "model", "rig", "retop", "cloth", "fur", "groom", "texture", "anim", "fx", "lookdev", "photoscan"];
    tasks.forEach(t => {
      if (!(biddingMode && t === "supervisor")) {
        total += colWidths[t];
      }
    });
    
    return total;
  }, [colWidths, biddingMode, readOnly]);

  const handleResizeStart = (e: React.MouseEvent, col: string) => {
    e.preventDefault();
    setResizing({
      col,
      startX: e.pageX,
      startWidth: colWidths[col] || 100,
    });
  };

  const calculateAssetTotal = useCallback((asset: Asset) => {
    const baseTaskCost =
      (asset.days.concept || 0) * (rates.concept || 0) +
      (asset.days.dmp || 0) * (rates.dmp || 0) +
      (asset.days.model || 0) * (rates.model || 0) +
      (asset.days.rig || 0) * (rates.rig || 0) +
      (asset.days.retop || 0) * (rates.retop || 0) +
      (asset.days.cloth || 0) * (rates.cloth || 0) +
      (asset.days.fur || 0) * (rates.fur || 0) +
      (asset.days.groom || 0) * (rates.groom || 0) +
      (asset.days.texture || 0) * (rates.texture || 0) +
      (asset.days.anim || 0) * (rates.anim || 0) +
      (asset.days.fx || 0) * (rates.fx || 0) +
      (asset.days.lookdev || 0) * (rates.lookdev || 0) +
      (asset.days.photoscan || 0) * (rates.photoscan || 0);
      
    const taskDays = 
      (asset.days.concept || 0) +
      (asset.days.dmp || 0) +
      (asset.days.model || 0) +
      (asset.days.rig || 0) +
      (asset.days.retop || 0) +
      (asset.days.cloth || 0) +
      (asset.days.fur || 0) +
      (asset.days.groom || 0) +
      (asset.days.texture || 0) +
      (asset.days.anim || 0) +
      (asset.days.fx || 0) +
      (asset.days.lookdev || 0) +
      (asset.days.photoscan || 0);
    
    const supDays = taskDays * ((setup.supervisionPercentage || 0) / 100);
    const supCost = supDays * (rates.supervisor || 0);
    const taskCost = baseTaskCost + supCost;
    return taskCost * (asset.quantity || 1);
  }, [rates, setup.supervisionPercentage]);

  const getFieldValue = useCallback((asset: Asset, field: string) => {
    if (field === 'totalDays') {
      return Object.values(asset.days).reduce((a, b) => a + (b || 0), 0);
    }
    if (field === 'totalCost') {
      return calculateAssetTotal(asset);
    }
    if (field.startsWith('days.')) {
      const task = field.split('.')[1] as AssetTaskType;
      return asset.days[task] || 0;
    }
    return (asset as any)[field];
  }, [calculateAssetTotal]);

  const selectedStatusSet = useMemo(() => new Set(selectedStatuses), [selectedStatuses]);
  const isStatusFilterActive = selectedStatuses.length < BID_ITEM_STATUSES.length;

  const filteredAssets = useMemo(() => {
    return assets.filter((a) => {
      // Basic search
      const searchMatch = !search || 
        a.assetCode.toLowerCase().includes(search.toLowerCase()) ||
        a.description.toLowerCase().includes(search.toLowerCase());
      
      if (!searchMatch) return false;

      const statusMatch = selectedStatusSet.has(getBidStatusValue(a));
      if (!statusMatch) return false;

      // Advanced filters
      if (advancedFilters.length === 0) return true;

      return advancedFilters.every((filter) => {
        const fieldValue = getFieldValue(a, filter.field);
        const filterValue = filter.value;

        switch (filter.operator) {
          case 'contains':
            return String(fieldValue).toLowerCase().includes(String(filterValue).toLowerCase());
          case 'not_contains':
            return !String(fieldValue).toLowerCase().includes(String(filterValue).toLowerCase());
          case 'equals':
            return String(fieldValue).toLowerCase() === String(filterValue).toLowerCase();
          case 'not_equals':
            return String(fieldValue).toLowerCase() !== String(filterValue).toLowerCase();
          case 'gt':
            return Number(fieldValue) > Number(filterValue);
          case 'lt':
            return Number(fieldValue) < Number(filterValue);
          case 'gte':
            return Number(fieldValue) >= Number(filterValue);
          case 'lte':
            return Number(fieldValue) <= Number(filterValue);
          case 'is_empty':
            return !fieldValue || fieldValue === '';
          case 'is_not_empty':
            return !!fieldValue && fieldValue !== '';
          default:
            return true;
        }
      });
    });
  }, [assets, search, advancedFilters, getFieldValue, selectedStatusSet]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target as Node)) {
        setIsStatusFilterOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleAssetChange = useCallback((id: string, field: keyof Asset, value: any) => {
    if (readOnly) return;

    onChange((prevAssets) =>
      prevAssets.map((a) => {
        if (a.id === id) {
          const updates: any = { [field]: value };
          if (field === "status") {
            updates.active = !isExcludedBidStatus(value);
            if (value === "Awarded" && a.originalAwardedValue === undefined) {
              updates.originalAwardedValue = calculateAssetTotal(a);
              updates.originalAwardedAt = new Date().toISOString();
            }
          }
          return { ...a, ...updates };
        }
        return a;
      })
    );
  }, [readOnly, onChange, calculateAssetTotal]);

  const handleGenerateAI = useCallback(async (id: string, description: string) => {
    if (readOnly || !description) return;
    
    setGeneratingIds(prev => new Set(prev).add(id));
    try {
      // Strip bullet points for cleaner prompt
      const cleanDescription = description.replace(/^•\s*/, '');
      const imageUrl = await generateThumbnail(cleanDescription, 'asset');
      if (imageUrl) {
        handleAssetChange(id, "thumbnail", imageUrl);
      }
    } catch (error) {
      console.error("AI generation failed:", error);
      toast.error("Failed to generate thumbnail. Please ensure you have a valid description.");
    } finally {
      setGeneratingIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }, [readOnly, handleAssetChange]);

  const handleDaysChange = useCallback((id: string, task: AssetTaskType, value: string) => {
    if (readOnly) return;
    const numValue = parseFloat(value);
    onChange((prevAssets) =>
      prevAssets.map((a) => {
        if (a.id === id) {
          return {
            ...a,
            days: {
              ...a.days,
              [task]: isNaN(numValue) || numValue < 0 ? 0 : numValue,
            },
          };
        }
        return a;
      })
    );
  }, [readOnly, onChange]);

  const handleManualSort = useCallback(() => {
    if (readOnly) return;
    onChange((prevAssets) => {
      return [...prevAssets].sort((a, b) => {
        if (a.type !== b.type) return a.type.localeCompare(b.type);
        return a.assetCode.localeCompare(b.assetCode);
      });
    });
  }, [readOnly, onChange]);

  const moveAsset = useCallback((index: number, direction: 'up' | 'down') => {
    if (readOnly) return;
    
    // If we are searching, we shouldn't allow reordering as it gets confusing
    if (search.trim() !== "") return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= assets.length) return;

    const newAssets = [...assets];
    const temp = newAssets[index];
    newAssets[index] = newAssets[newIndex];
    newAssets[newIndex] = temp;
    
    onChange(newAssets);
  }, [readOnly, search, assets, onChange]);

  const handleTextareaKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>, id: string, field: keyof Asset, rawValue: string | undefined) => {
    // Normal textarea behavior for Enter key
    // We don't need to intercept it anymore since we removed auto-bullets
  }, [handleAssetChange]);

  const handleRowDoubleClick = useCallback((asset: Asset, e: React.MouseEvent) => {
    // Don't open if clicking on interactive elements
    const target = e.target as HTMLElement;
    if (
      target.tagName === "INPUT" || 
      target.tagName === "TEXTAREA" || 
      target.tagName === "BUTTON" || 
      target.closest("button") || 
      target.closest("label")
    ) {
      return;
    }
    setSelectedAssetIdForDetail(asset.id);
  }, []);

  const handleNavigateDetail = (direction: 'prev' | 'next') => {
    if (!selectedAssetIdForDetail) return;
    const currentIndex = filteredAssets.findIndex(a => a.id === selectedAssetIdForDetail);
    if (currentIndex === -1) return;
    
    if (direction === 'prev' && currentIndex > 0) {
      setSelectedAssetIdForDetail(filteredAssets[currentIndex - 1].id);
    } else if (direction === 'next' && currentIndex < filteredAssets.length - 1) {
      setSelectedAssetIdForDetail(filteredAssets[currentIndex + 1].id);
    }
  };

  const selectedAssetForDetail = useMemo(() => {
    return assets.find(a => a.id === selectedAssetIdForDetail) || null;
  }, [assets, selectedAssetIdForDetail]);

  const toggleSelectAll = useCallback(() => {
    if (selectedIds.size === filteredAssets.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredAssets.map((a) => a.id)));
    }
  }, [selectedIds.size, filteredAssets]);

  const toggleSelect = useCallback((id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  }, [selectedIds]);

  const handleSmartFilter = async () => {
    if (!smartFilter.trim() || !process.env.GEMINI_API_KEY) return;
    
    setIsSmartFiltering(true);
    try {
      const ai = new GoogleGenAI({ apiKey: process.env.GEMINI_API_KEY });
      const response = await ai.models.generateContent({
        model: "gemini-3-flash-preview",
        contents: `Convert this natural language filter query into a JSON array of filter criteria for a VFX asset list.
        Query: "${smartFilter}"
        
        Available fields: ${FILTER_FIELDS.map(f => f.value).join(', ')}
        Available operators: ${OPERATORS.map(o => o.value).join(', ')}
        
        Return ONLY a JSON array of objects with fields: { field, operator, value }.
        Example: "Active assets with > 5 days" -> [{"field": "active", "operator": "equals", "value": "true"}, {"field": "totalDays", "operator": "gt", "value": "5"}]`,
        config: {
          responseMimeType: "application/json",
        }
      });

      const result = JSON.parse(response.text);
      if (Array.isArray(result)) {
        const newFilters: FilterCriterion[] = result.map((f: any) => ({
          id: Math.random().toString(36).substr(2, 9),
          field: f.field,
          operator: f.operator,
          value: String(f.value),
        }));
        setAdvancedFilters(newFilters);
        setIsFilterPanelOpen(true);
        setSmartFilter("");
      }
    } catch (error) {
      console.error("Smart filter error:", error);
      toast.error("Failed to parse smart filter. Try a simpler query.");
    } finally {
      setIsSmartFiltering(false);
    }
  };

  const addFilter = () => {
    setAdvancedFilters([
      ...advancedFilters,
      { id: Math.random().toString(36).substr(2, 9), field: 'assetCode', operator: 'contains', value: '' }
    ]);
  };

  const removeFilter = (id: string) => {
    setAdvancedFilters(advancedFilters.filter(f => f.id !== id));
  };

  const updateFilter = (id: string, updates: Partial<FilterCriterion>) => {
    setAdvancedFilters(advancedFilters.map(f => f.id === id ? { ...f, ...updates } : f));
  };

  const clearFilters = () => {
    setAdvancedFilters([]);
    setSearch("");
    setSelectedStatuses(BID_ITEM_STATUSES);
  };

  const toggleStatusFilter = useCallback((status: ShotStatus) => {
    setSelectedStatuses((prev) =>
      prev.includes(status) ? prev.filter((value) => value !== status) : [...prev, status]
    );
  }, []);

  const selectAllStatuses = useCallback(() => {
    setSelectedStatuses(BID_ITEM_STATUSES);
  }, []);

  const filteredTotal = filteredAssets.reduce(
    (acc, asset) => acc + (isBidItemIncluded(asset) ? calculateAssetTotal(asset) : 0),
    0
  );

  useEffect(() => {
    onFilterStateChange?.({
      isActive: Boolean(search.trim() || advancedFilters.length > 0 || isStatusFilterActive),
      filteredIds: filteredAssets.map((asset) => asset.id),
      search,
      advancedFilterCount: advancedFilters.length + (isStatusFilterActive ? 1 : 0),
      visibleCount: filteredAssets.length,
      totalCount: assets.length,
    });
  }, [onFilterStateChange, search, advancedFilters.length, filteredAssets, assets.length, isStatusFilterActive]);

  const handleBulkApply = (updates: any) => {
    if (readOnly) return;
    const updatedAssets = assets.map((a) => {
      if (selectedIds.has(a.id)) {
        const newDays = { ...a.days };
        (["concept", "dmp", "model", "rig", "retop", "cloth", "fur", "groom", "texture", "anim", "fx", "lookdev", "photoscan"] as AssetTaskType[]).forEach((task) => {
          if (updates[task] !== undefined) {
            newDays[task] = updates[task];
          }
        });
        return {
          ...a,
          days: newDays,
          assumptions: updates.assumptions !== undefined ? updates.assumptions : a.assumptions,
          internalNotes: updates.internalNotes !== undefined ? updates.internalNotes : a.internalNotes,
          status: updates.status !== undefined ? updates.status : a.status,
          active: updates.status !== undefined ? !isExcludedBidStatus(updates.status) : a.active,
        };
      }
      return a;
    });
    onChange(updatedAssets);
    setIsBulkEditOpen(false);
    setSelectedIds(new Set());
  };

  const handleBulkDelete = () => {
    if (readOnly) return;
    const count = selectedIds.size;
    const newAssets = assets.filter((a) => !selectedIds.has(a.id));
    onChange(newAssets);
    setIsBulkEditOpen(false);
    setSelectedIds(new Set());
    toast.success(`Deleted ${count} assets`);
  };

  const handleThumbnailUpload = useCallback((id: string, e: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly) return;
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const targetWidth = 480;
          const targetHeight = 270;
          canvas.width = targetWidth;
          canvas.height = targetHeight;
          const ctx = canvas.getContext("2d");

          if (ctx) {
            // Calculate cropping to maintain aspect ratio (cover)
            const imgAspectRatio = img.width / img.height;
            const targetAspectRatio = targetWidth / targetHeight;
            let drawWidth, drawHeight, offsetX, offsetY;

            if (imgAspectRatio > targetAspectRatio) {
              // Image is wider than target
              drawHeight = targetHeight;
              drawWidth = img.width * (targetHeight / img.height);
              offsetX = (targetWidth - drawWidth) / 2;
              offsetY = 0;
            } else {
              // Image is taller than target
              drawWidth = targetWidth;
              drawHeight = img.height * (targetWidth / img.width);
              offsetX = 0;
              offsetY = (targetHeight - drawHeight) / 2;
            }

            ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
            const croppedDataUrl = canvas.toDataURL("image/jpeg", 0.85);
            handleAssetChange(id, "thumbnail", croppedDataUrl);
          }
        };
        img.src = event.target?.result as string;
      };
      reader.readAsDataURL(file);
    }
  }, [readOnly, handleAssetChange]);

  const assetIndices = useMemo(() => {
    const map = new Map<string, number>();
    filteredAssets.forEach((a, i) => map.set(a.id, i));
    return map;
  }, [filteredAssets]);

  const assetIds = useMemo(() => filteredAssets.map(a => a.id), [filteredAssets]);

  return (
    <div className="flex flex-col h-full">
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col h-full">
        <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between gap-4 shrink-0">
          <div className="flex items-center gap-3">
            <SectionPillHeader icon={Database} title="Asset Bidding" />
          </div>

          <div className="flex items-center gap-3 flex-1 justify-end">
            {selectedIds.size > 0 && !readOnly && (
              <button
                onClick={() => setIsBulkEditOpen(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400 text-xs font-medium rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors"
              >
                <Edit3 className="w-3.5 h-3.5" />
                Bulk Edit ({selectedIds.size})
              </button>
            )}
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <input
                type="text"
                placeholder="Search assets..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-xs bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
              />
            </div>
            <button
              onClick={() => setBiddingMode(!biddingMode)}
              className={cn(
                "group relative flex items-center justify-center w-8 h-8 rounded-lg border transition-colors",
                biddingMode 
                  ? "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400" 
                  : "bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
              )}
              title="Bidding View"
            >
              <FoldVertical className={cn("w-3.5 h-3.5", biddingMode && "fill-current")} />
            </button>
            <button
              onClick={handleManualSort}
              className="group relative flex items-center justify-center w-8 h-8 rounded-lg border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              title="Sort Assets"
            >
              <ArrowUpDown className="w-3.5 h-3.5" />
            </button>
            <div className="relative" ref={statusFilterRef}>
              <button
                onClick={() => setIsStatusFilterOpen((prev) => !prev)}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors border",
                  isStatusFilterOpen || isStatusFilterActive
                    ? "bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400 border-indigo-200 dark:border-indigo-800"
                    : "bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700"
                )}
                title="Filter by status"
              >
                <Filter className="w-3.5 h-3.5" />
                <span>Status</span>
                {isStatusFilterActive && (
                  <span className="rounded-full bg-white/80 dark:bg-slate-900/80 px-1.5 py-0.5 text-[10px] leading-none">
                    {selectedStatuses.length}
                  </span>
                )}
                <ChevronDown className={cn("w-3.5 h-3.5 transition-transform", isStatusFilterOpen && "rotate-180")} />
              </button>
              {isStatusFilterOpen && (
                <div className="absolute right-0 top-full z-30 mt-2 w-52 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg p-2">
                  <div className="flex items-center justify-between px-2 py-1.5 text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-[0.16em]">
                    <span>Status Filter</span>
                    <button
                      onClick={selectAllStatuses}
                      className="text-[10px] font-medium tracking-normal normal-case text-indigo-600 dark:text-indigo-300 hover:text-indigo-700 dark:hover:text-indigo-200"
                    >
                      Select all
                    </button>
                  </div>
                  <div className="mt-1 flex flex-col">
                    {BID_ITEM_STATUSES.map((status) => {
                      const checked = selectedStatusSet.has(status);
                      return (
                        <label
                          key={status}
                          className={cn(
                            "flex items-center gap-2 rounded-lg px-2 py-2 text-xs border cursor-pointer transition-colors",
                            getBidStatusClasses(status),
                            checked ? "ring-1 ring-inset ring-current/15" : "opacity-85 hover:opacity-100"
                          )}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleStatusFilter(status)}
                            className="h-3.5 w-3.5 rounded border-slate-300 text-slate-500 focus:ring-slate-400"
                          />
                          <span>{status}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <button
              onClick={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
              className={cn(
                "flex items-center justify-center w-8 h-8 rounded-lg transition-colors border",
                isFilterPanelOpen || advancedFilters.length > 0
                  ? "bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400 border-indigo-200 dark:border-amber-800"
                  : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700"
              )}
              title={advancedFilters.length > 0 ? `Filters (${advancedFilters.length})` : ""}
            >
              <Filter className="w-3.5 h-3.5" />
            </button>
            {!readOnly && (
              <button
                onClick={onAddAsset}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#ffcc01] text-slate-900 text-xs font-medium rounded-lg hover:bg-[#e6b801] transition-colors shadow-sm"
              >
                <Plus className="w-4 h-4" />
                Add Asset
              </button>
            )}
          </div>
        </div>

        {(isFilterPanelOpen || advancedFilters.length > 0) && (
          <div className="bg-slate-50 dark:bg-slate-950/60 rounded-lg p-3 border border-slate-200 dark:border-slate-800 animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-700 dark:text-slate-200">
                  <Filter className="w-3.5 h-3.5" />
                  Advanced Filters
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={clearFilters}
                    className="text-[10px] text-slate-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-300 font-medium transition-colors"
                  >
                    Clear All
                  </button>
                  <button
                    onClick={() => setIsFilterPanelOpen(false)}
                    className="p-1 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-200 transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                {advancedFilters.map((filter) => (
                  <div key={filter.id} className="flex flex-wrap items-center gap-2 bg-white dark:bg-slate-900 p-1.5 rounded-md border border-slate-200 dark:border-slate-700 shadow-sm">
                    <select
                      value={filter.field}
                      onChange={(e) => updateFilter(filter.id, { field: e.target.value })}
                      className="text-[10px] border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 rounded px-1.5 py-0.5 outline-none focus:ring-1 focus:ring-indigo-500"
                    >
                      {FILTER_FIELDS.map((f) => (
                        <option key={f.value} value={f.value}>{f.label}</option>
                      ))}
                    </select>
                    <select
                      value={filter.operator}
                      onChange={(e) => updateFilter(filter.id, { operator: e.target.value as any })}
                      className="text-[10px] border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 rounded px-1.5 py-0.5 outline-none focus:ring-1 focus:ring-indigo-500"
                    >
                      {OPERATORS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                    {!['is_empty', 'is_not_empty'].includes(filter.operator) && (
                      <input
                        type="text"
                        value={filter.value}
                        onChange={(e) => updateFilter(filter.id, { value: e.target.value })}
                        placeholder="Value..."
                        className="text-[10px] border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 rounded px-1.5 py-0.5 outline-none focus:ring-1 focus:ring-indigo-500 flex-1 min-w-[80px]"
                      />
                    )}
                    <button
                      onClick={() => removeFilter(filter.id)}
                      className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-3 pt-1.5 border-t border-slate-200 dark:border-slate-800">
                <button
                  onClick={addFilter}
                  className="flex items-center gap-1 text-[10px] font-medium text-indigo-600 hover:text-indigo-700 transition-colors whitespace-nowrap"
                >
                  <Plus className="w-3 h-3" />
                  Add Filter
                </button>
                <div className="h-3 w-px bg-slate-200 dark:bg-slate-800 hidden sm:block" />
                <div className="relative flex-1 w-full">
                  <Sparkles className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-indigo-500" />
                  <input
                    type="text"
                    placeholder="Smart Filter (e.g., 'Assets with rigging in description')"
                    value={smartFilter}
                    onChange={(e) => setSmartFilter(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSmartFilter()}
                    className="w-full pl-7 pr-4 py-1 text-[10px] border border-indigo-200 dark:border-indigo-800 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-indigo-50/50 dark:bg-indigo-950/30 text-slate-700 dark:text-slate-200"
                  />
                  {isSmartFiltering && (
                    <div className="absolute right-2 top-1/2 -translate-y-1/2">
                      <div className="w-3 h-3 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div ref={scrollContainerRef} className="flex-1 overflow-auto pb-4">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <table className="w-full text-sm text-left table-fixed border-separate border-spacing-0" style={{ minWidth: visibleWidth }}>
            <thead className="text-xs text-slate-500 dark:text-slate-400 uppercase bg-slate-50 dark:bg-slate-950 sticky top-0 z-20 whitespace-nowrap">
            <tr>
              <ResizableTh 
                id="select" 
                width={colWidths.select} 
                onResizeStart={handleResizeStart} 
                className="px-4 py-3 sticky left-0 z-30 bg-slate-50 dark:bg-slate-950 border-l-2 border-r-2 border-b-2 border-white dark:border-slate-900"
                style={{ left: 0 }}
              >
                <button onClick={toggleSelectAll} className="flex w-full items-center justify-center text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-200" disabled={readOnly}>
                  {selectedIds.size === filteredAssets.length && filteredAssets.length > 0 ? (
                    <CheckSquare className="w-4 h-4" />
                  ) : (
                    <Square className="w-4 h-4" />
                  )}
                </button>
              </ResizableTh>
              <ResizableTh 
                id="active" 
                width={colWidths.active} 
                onResizeStart={handleResizeStart} 
                className="px-4 py-3 text-center sticky z-30 bg-slate-50 dark:bg-slate-950 border-r-2 border-b-2 border-white dark:border-slate-900"
                style={{ left: colWidths.select }}
              >
                Status
              </ResizableTh>
              <ResizableTh 
                id="thumb" 
                width={biddingMode ? colWidths.thumb - 20 : colWidths.thumb} 
                onResizeStart={handleResizeStart} 
                className="px-4 py-3 text-center sticky z-30 bg-slate-50 dark:bg-slate-950 border-r-2 border-b-2 border-white dark:border-slate-900"
                style={{ left: colWidths.select + colWidths.active }}
              >
                Thumbnail
              </ResizableTh>
              <ResizableTh 
                id="assetCode" 
                width={colWidths.assetCode} 
                onResizeStart={handleResizeStart} 
                className="px-4 py-3 sticky z-30 bg-slate-50 dark:bg-slate-950 border-r-2 border-b-2 border-white dark:border-slate-900"
                style={{ left: colWidths.select + colWidths.active + (biddingMode ? colWidths.thumb - 20 : colWidths.thumb) }}
              >
                Asset Name
              </ResizableTh>
              {!biddingMode && (
                <ResizableTh 
                  id="type" 
                  width={colWidths.type} 
                  onResizeStart={handleResizeStart} 
                  className="px-4 py-3 bg-slate-50 dark:bg-slate-950 sticky z-30 border-r-2 border-b-2 border-white dark:border-slate-900"
                  style={{ left: colWidths.select + colWidths.active + colWidths.thumb + colWidths.assetCode }}
                >
                  Type
                </ResizableTh>
              )}
              <ResizableTh 
                id="total" 
                width={colWidths.total} 
                onResizeStart={handleResizeStart} 
                className={cn("px-4 py-3 text-right sticky z-30 bg-slate-50 dark:bg-slate-950 border-r-2 border-b-2 border-white dark:border-slate-900")}
                style={{ left: colWidths.select + colWidths.active + (biddingMode ? colWidths.thumb - 20 : colWidths.thumb) + colWidths.assetCode + (biddingMode ? 0 : colWidths.type) }}
              >
                Total Cost
              </ResizableTh>
              {!biddingMode && (
                <>
                  <ResizableTh id="description" width={colWidths.description} onResizeStart={handleResizeStart} className="px-4 py-3 border-r-2 border-b-2 border-white dark:border-slate-900">Description</ResizableTh>
                  <ResizableTh id="qty" width={colWidths.qty} onResizeStart={handleResizeStart} className="px-4 py-3 text-center border-r-2 border-b-2 border-white dark:border-slate-900">Qty</ResizableTh>
                </>
              )}
              <ResizableTh id="supervisor" width={colWidths.supervisor} onResizeStart={handleResizeStart} className="px-4 py-3 text-center bg-slate-50 dark:bg-slate-950 border-l-2 border-r-2 border-b-2 border-white dark:border-slate-900" title="Supervision">Sup</ResizableTh>
              <ResizableTh 
                id="concept" 
                width={colWidths.concept} 
                onResizeStart={handleResizeStart} 
                className={cn(
                  "px-1 py-3 text-center bg-slate-50 dark:bg-slate-950 border-r-2 border-b-2 border-white dark:border-slate-900",
                  biddingMode && "border-l-2"
                )} 
                title="Concept"
              >
                Concept
              </ResizableTh>
              <ResizableTh id="dmp" width={colWidths.dmp} onResizeStart={handleResizeStart} className="px-1 py-3 text-center bg-slate-50 dark:bg-slate-950 border-r-2 border-b-2 border-white dark:border-slate-900" title="DMP">DMP</ResizableTh>
              <ResizableTh id="model" width={colWidths.model} onResizeStart={handleResizeStart} className="px-1 py-3 text-center bg-slate-50 dark:bg-slate-950 border-r-2 border-b-2 border-white dark:border-slate-900" title="Model">Model</ResizableTh>
              <ResizableTh id="rig" width={colWidths.rig} onResizeStart={handleResizeStart} className="px-1 py-3 text-center bg-slate-50 dark:bg-slate-950 border-r-2 border-b-2 border-white dark:border-slate-900" title="Rig">Rig</ResizableTh>
              <ResizableTh id="retop" width={colWidths.retop} onResizeStart={handleResizeStart} className="px-1 py-3 text-center bg-slate-50 dark:bg-slate-950 border-r-2 border-b-2 border-white dark:border-slate-900" title="Retop">Retop</ResizableTh>
              <ResizableTh id="cloth" width={colWidths.cloth} onResizeStart={handleResizeStart} className="px-1 py-3 text-center bg-slate-50 dark:bg-slate-950 border-r-2 border-b-2 border-white dark:border-slate-900" title="Cloth">Cloth</ResizableTh>
              <ResizableTh id="fur" width={colWidths.fur} onResizeStart={handleResizeStart} className="px-1 py-3 text-center bg-slate-50 dark:bg-slate-950 border-r-2 border-b-2 border-white dark:border-slate-900" title="Fur">Fur</ResizableTh>
              <ResizableTh id="groom" width={colWidths.groom} onResizeStart={handleResizeStart} className="px-1 py-3 text-center bg-slate-50 dark:bg-slate-950 border-r-2 border-b-2 border-white dark:border-slate-900" title="Groom">Groom</ResizableTh>
              <ResizableTh id="texture" width={colWidths.texture} onResizeStart={handleResizeStart} className="px-1 py-3 text-center bg-slate-50 dark:bg-slate-950 border-r-2 border-b-2 border-white dark:border-slate-900" title="Texture">Texture</ResizableTh>
              <ResizableTh id="anim" width={colWidths.anim} onResizeStart={handleResizeStart} className="px-1 py-3 text-center bg-slate-50 dark:bg-slate-950 border-r-2 border-b-2 border-white dark:border-slate-900" title="Anim">Anim</ResizableTh>
              <ResizableTh id="fx" width={colWidths.fx} onResizeStart={handleResizeStart} className="px-1 py-3 text-center bg-slate-50 dark:bg-slate-950 border-r-2 border-b-2 border-white dark:border-slate-900" title="FX">FX</ResizableTh>
              <ResizableTh id="lookdev" width={colWidths.lookdev} onResizeStart={handleResizeStart} className="px-1 py-3 text-center bg-slate-50 dark:bg-slate-950 border-r-2 border-b-2 border-white dark:border-slate-900" title="Lookdev">Lookdev</ResizableTh>
              <ResizableTh id="photoscan" width={colWidths.photoscan} onResizeStart={handleResizeStart} className="px-1 py-3 text-center bg-slate-50 dark:bg-slate-950 border-r-2 border-b-2 border-white dark:border-slate-900" title="Photoscan">Scan</ResizableTh>
              {!biddingMode && (
                <>
                  <ResizableTh id="assumptions" width={colWidths.assumptions} onResizeStart={handleResizeStart} className="px-4 py-3 border-r-2 border-b-2 border-white dark:border-slate-900">Assumptions</ResizableTh>
                  <ResizableTh id="scopeOfWork" width={colWidths.scopeOfWork} onResizeStart={handleResizeStart} className="px-4 py-3 border-r-2 border-b-2 border-white dark:border-slate-900">Scope of Work</ResizableTh>
                  <ResizableTh id="internalNotes" width={colWidths.internalNotes} onResizeStart={handleResizeStart} className="px-4 py-3 border-r-2 border-b-2 border-white dark:border-slate-900">Internal Notes</ResizableTh>
                </>
              )}
              {!readOnly && <ResizableTh id="actions" width={colWidths.actions} onResizeStart={handleResizeStart} className="px-4 py-3 text-center border-r-2 border-b-2 border-white dark:border-slate-900">Actions</ResizableTh>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            <SortableContext
              items={assetIds}
              strategy={verticalListSortingStrategy}
            >
              {filteredAssets.map((asset) => (
                <SortableAssetRow
                  key={asset.id}
                  asset={asset}
                  index={assetIndices.get(asset.id) || 0}
                  calculateAssetTotal={calculateAssetTotal}
                  isSelected={selectedIds.has(asset.id)}
                  toggleSelect={toggleSelect}
                  handleAssetChange={handleAssetChange}
                  handleDaysChange={handleDaysChange}
                  handleThumbnailUpload={handleThumbnailUpload}
                  handleGenerateAI={handleGenerateAI}
                  handleRowDoubleClick={handleRowDoubleClick}
                  handleTextareaKeyDown={handleTextareaKeyDown}
                  moveAsset={moveAsset}
                  onDuplicateAsset={onDuplicateAsset}
                  onDeleteAsset={onDeleteAsset}
                  readOnly={readOnly}
                  colWidths={colWidths}
                  setup={setup}
                  search={search}
                  filteredAssetsCount={filteredAssets.length}
                  generatingIds={generatingIds}
                  rates={rates}
                  biddingMode={biddingMode}
                />
              ))}
            </SortableContext>
            {filteredAssets.length === 0 && (
              <tr>
                <td colSpan={readOnly ? 24 : 25} className="px-4 py-8 text-center text-slate-500 dark:text-slate-400">
                  <div className="sticky left-0 w-full text-center">
                    No assets found. Add an asset to get started.
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </DndContext>
      </div>

      <div className="bg-slate-50 dark:bg-slate-950 border-t border-slate-200 dark:border-slate-800 py-1.5 px-4 flex justify-end items-center gap-6 z-20">
        <div className="text-right font-semibold text-slate-500 dark:text-slate-400 text-xs uppercase tracking-wider">
          Subtotal (Active Status)
        </div>
        <div className="text-right font-bold text-slate-900 dark:text-slate-100">
          <div className="text-sm">{formatCurrency(filteredTotal, setup.currency)}</div>
          {setup.targetCurrency && setup.exchangeRate > 0 && (
            <div className="text-xs text-slate-500 dark:text-slate-400 font-medium">
              {formatCurrency(filteredTotal * setup.exchangeRate, setup.targetCurrency)}
            </div>
          )}
        </div>
      </div>
      </div>

      {isBulkEditOpen && !readOnly && (
        <AssetBulkEditModal
          onClose={() => setIsBulkEditOpen(false)}
          onApply={handleBulkApply}
          onDelete={handleBulkDelete}
          selectedCount={selectedIds.size}
        />
      )}

      {selectedAssetForDetail && (
        <AssetDetailModal
          asset={selectedAssetForDetail}
          isOpen={!!selectedAssetForDetail}
          onClose={() => setSelectedAssetIdForDetail(null)}
          onUpdate={handleAssetChange}
          onNavigate={handleNavigateDetail}
          hasPrev={filteredAssets.findIndex(a => a.id === selectedAssetIdForDetail) > 0}
          hasNext={filteredAssets.findIndex(a => a.id === selectedAssetIdForDetail) < filteredAssets.length - 1}
          readOnly={readOnly}
          rates={rates}
          setup={setup}
        />
      )}
    </div>
  );
}
